import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';

/**
 * Dragging a card from the hand onto the board. A spot that can take the card being played registers what a tap on
 * it would do (useDropTarget), under its place's name (Motion/anchorNames); dropping on that place does it. So the
 * board decides where a card can go the same way for a tap and for a drop.
 */
const targets = new Map();

export function useDropTarget(name, commit) {
  useEffect(() => {
    if (!commit) {
      return undefined;
    }
    targets.set(name, commit);
    return () => {
      if (targets.get(name) === commit) {
        targets.delete(name);
      }
    };
  }, [name, commit]);
}

/** The whole board, as a place to drop a card that goes nowhere in particular (a Trainer like Bill). */
export const BOARD_DROP = 'board';

/** What's registered at a place, if anything. */
export const dropTargetAt = (name) => targets.get(name) || null;

/** The card being dragged now ({ kind, handPosition }) or null, for spots that only light up while dragging. */
let dragging = null;
const listeners = new Set();

export function setDragging(next) {
  dragging = next;
  listeners.forEach(listener => listener());
}

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useDragging = () => useSyncExternalStore(subscribe, () => dragging);

/** How long a dropped card is remembered while the server answers. */
const DROP_MEMORY_MS = 5000;

/**
 * The hand place of the last card you dragged and let go on the board. You already put it there, so it doesn't fly
 * there again when the game answers (FlyingCards asks, once).
 */
let lastDrop = null;
export function rememberDrop(from) {
  lastDrop = { from, at: Date.now() };
}
export function takeDrop(from) {
  if (lastDrop && lastDrop.from === from && Date.now() - lastDrop.at < DROP_MEMORY_MS) {
    lastDrop = null;
    return true;
  }
  return false;
}

/**
 * Keeps an element the height it was before a drag started, until the drag's flow is over. The turn banner changes
 * its words when a drag starts its flow ("Bench a Pokémon"), and the board would otherwise resize under the finger;
 * and once the card is let go the flow lasts until the game answers, so the banner would shrink for a moment and
 * the board jump. While the finger is down the height is fixed; after, it can grow (a drop that asks something
 * more) but not shrink, until `inFlow` is false.
 *
 * @param {boolean} inFlow whether the flow the drag started is still going (the banner's board choice)
 * @returns {[object, object|undefined]} a ref for the element, and the style to give it
 */
export function useHeightHeldWhileDragging(inFlow) {
  const dragging = useDragging();
  const ref = useRef(null);
  const before = useRef(null);
  const held = useRef(false);

  if (dragging && before.current) {
    held.current = true;
  } else if (!dragging && !inFlow) {
    held.current = false;
  }

  useLayoutEffect(() => {
    if (!held.current && ref.current) {
      before.current = ref.current.offsetHeight;
    }
  });

  if (!held.current) {
    return [ref, undefined];
  }
  return [ref, dragging
    ? { height: before.current, boxSizing: 'border-box', overflow: 'hidden' }
    : { minHeight: before.current, boxSizing: 'border-box' }];
}
